

import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Grid,
  Column,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  ToastNotification,
  Button,
} from "@carbon/react";

// Step Components
import PropertyDetails from "./PropertyDetails";
import RentalDetails from "./RentalDetails";
import LocationDetails from "../../common/LocationDetails";
import Aminities from "./Aminities";
import Gallery from "../../common/Gallery";
import AdditionalInfo from "../sale/AdditionalInfo";
import Schedule from "../../common/Schedule";
import { callApi } from "../../../../services/apiService";
import { generatePropertyTitle } from "../../../../utils/PropertyTitle";

export default function CommercialRentForm({
  property_uuid = "",
  propertyDetails = {},
  isEditMode = false,
  currentStep = 1,
  onSaveSuccess,
  onStepChange,
  lastSavedStep = 0,
  request_type = "form_attributes",
  formMode = "add",
}) {
  // const navigate = useNavigate();
  const location = useLocation();

  const contactDataRef = useRef(null);

  // ---------- URL state ----------
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlPropertyUUID = searchParams.get("property_uuid");
  const urlFormType = searchParams.get("form_type");

  // ---------- State ----------
  const [attributeValues, setAttributeValues] = useState([]);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [contactData, setContactData] = useState(null);

  const [propertyId, setPropertyId] = useState(null);
const [propertyFormData, setPropertyFormData] = useState({});

  const stepIndex = currentStep - 1;

  const effectiveFormMode = urlFormType || formMode;
  const isEditForm = effectiveFormMode === "edit";

  // DEBUG: Log mode calculation
  console.log('🔍 CommercialRentForm Mode Calculation:', {
    urlFormType,
    parentFormMode: formMode,
    effectiveFormMode,
    isEditForm,
    currentStep,
    lastSavedStep
  });

  const effectivePropertyUUID = useMemo(
    () => urlPropertyUUID || property_uuid,
    [urlPropertyUUID, property_uuid]
  );

  // ---------- Helpers: normalize ----------
  const toSnake = useCallback((s) =>
    String(s || "")
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/[\s-]+/g, "_")
      .replace(/__+/g, "_")
      .toLowerCase()
  , []);

  const normalizeAttrKeys = useCallback((obj) => {
    if (!obj || typeof obj !== "object") return {};
    const out = {};
    Object.keys(obj).forEach((k) => {
      out[toSnake(k)] = obj[k];
    });
    return out;
  }, [toSnake]);

  const extractPropertyObject = useCallback((raw) => {
    if (!raw) return {};
    if (Array.isArray(raw)) return raw[0] || {};
    if (Array.isArray(raw?.data)) return raw.data[0] || {};
    if (Array.isArray(raw?.data?.data)) return raw.data.data[0] || {};
    return raw;
  }, []);

  const normalizedPropertyDetails = useMemo(() => {
    const base = extractPropertyObject(propertyDetails);
    const normalized = {
      ...base,
      attributes: normalizeAttrKeys(base.attributes || {}),
      area_information: normalizeAttrKeys(base.area_information || {}),
    };
    return normalized;
  }, [propertyDetails, extractPropertyObject, normalizeAttrKeys]);

    useEffect(() => {
    const attrs = normalizedPropertyDetails?.attributes;
    if (!attrs || attrs.com_rent_property_type === undefined) return;

    setPropertyFormData((prev) =>
      prev.propertyType !== undefined
        ? prev
        : { ...prev, propertyType: attrs.com_rent_property_type }
    );
  }, [normalizedPropertyDetails]);

  // ---------- Contact data from router state ----------
  useEffect(() => {
    if (location.state?.contactData) {
      console.log('📥 CommercialRentForm: Capturing contactData from location.state:', location.state.contactData);
      contactDataRef.current = location.state.contactData;  // ✅ Set ref immediately
      setContactData(location.state.contactData);
    }
  }, [location.state]);

  // ---------- Fetch attribute values ----------
  const fetchAttributeValues = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await callApi("GET", "/attribute-values/");
      if (res?.data) {
        const mapped = res.data.map((attr) => ({
          id: attr.id,
          label: attr.attr_val_name,
          value: attr.id,
          attrName: attr.attr_name,
          isActive: true,
        }));
        setAttributeValues(mapped);
      }
    } catch (err) {
      console.error("attribute-values fetch failed:", err);
      setError("Failed to load form options. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ---------- Fetch contact data ----------
  const fetchContactFromBackend = useCallback(async (uuid) => {
    if (!uuid) return null;
    try {
      const resp = await callApi("GET", `/property-contacts/?property_uuid=${uuid}`);
      let raw = null;

      if (resp?.data && Array.isArray(resp.data) && resp.data.length > 0) {
        raw = resp.data[0];
      } else if (resp?.data && typeof resp.data === "object" && !Array.isArray(resp.data)) {
        raw = resp.data;
      }

      if (!raw) return null;

      // Normalize backend keys (uppercase from POST) into the camelCase
      // shape the Contact modal and save payload expect.
      return {
        contactId:
          raw.contact_id ?? raw.CONTACT_ID ?? raw.PROPERTY_CONTACTS_ID ?? "",
        postingBy:
          raw.postingBy ?? raw.PROPERTY_POSTED_BY ?? "",
        contactPerson:
          raw.contactPerson ?? raw.PROPERTY_CONTACT_PERSON ?? "",
        contactPersonNumber:
          raw.contactPersonNumber ??
          (raw.PROPERTY_CONTACT_PERSON_NUMBER ? "Yes" : "No"),
        contactPersonContact:
          raw.contactPersonContact ?? raw.PROPERTY_CONTACT_PERSON_NUMBER ?? "",
        contactPersonEmail:
          raw.contactPersonEmail ?? raw.PROPERTY_CONTACT_PERSON_EMAIL ?? "",
        appt_availability:
          raw.appt_availability ?? raw.APPT_AVAILABILITY ?? "",
        appt_start_time:
          raw.appt_start_time ?? raw.APPT_AVAILBLE_START_TIME ?? "",
        appt_end_time:
          raw.appt_end_time ?? raw.APPT_AVAIALBLE_END_TIME ?? "",
        appt_all_day:
          raw.appt_all_day ?? raw.APPT_AVAILABLE_ALL_DAY ?? "",
      };
    } catch (err) {
      console.error("Failed to fetch contact from backend:", err);
      return null;
    }
  }, []);

  // ---------- Sync contact data when property UUID changes ----------
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!effectivePropertyUUID) return;
      try {
        const backendContact = await fetchContactFromBackend(effectivePropertyUUID);
        if (!mounted) return;
        if (backendContact) {
          setContactData((prev) => ({ ...prev, ...backendContact }));
        }
      } catch (err) {
        // already logged in fetchContactFromBackend
      }
    })();
    return () => { mounted = false; };
  }, [effectivePropertyUUID, fetchContactFromBackend]);

  // ---------- Save contact details ----------
  const saveContactDetails = useCallback(
    async (uuid, explicitContactData = null) => {
      const dataToUse = explicitContactData || contactDataRef.current || contactData;

      if (!dataToUse || !uuid) {
        console.warn('⚠️ saveContactDetails: No contact data available', {
          hasExplicit: !!explicitContactData,
          hasRef: !!contactDataRef.current,
          hasState: !!contactData,
          uuid
        });
        return;
      }

      try {
        const payload = {
          property_uuid: uuid,
          contact: {
            PROPERTY_POSTED_BY: dataToUse.postingBy || dataToUse.PROPERTY_POSTED_BY || "",
            PROPERTY_CONTACT_PERSON: dataToUse.contactPerson || "",
            PROPERTY_CONTACT_PERSON_NUMBER:
              (dataToUse.contactPersonNumber === "Yes" ? dataToUse.contactPersonContact : "") ||
              dataToUse.PROPERTY_CONTACT_PERSON_NUMBER ||
              "",
            PROPERTY_CONTACT_PERSON_EMAIL:
              (dataToUse.contactPersonNumber === "Yes" ? dataToUse.contactPersonEmail : "") ||
              dataToUse.PROPERTY_CONTACT_PERSON_EMAIL ||
              "",
            UPDATED_BY_USER_ID: 1,
          }
        };

        console.log('💾 Saving contact with payload:', payload);
        await callApi("POST", "/property-contacts/", payload);

        const saved = await fetchContactFromBackend(uuid);
        if (saved) setContactData((prev) => ({ ...prev, ...saved }));
      } catch (err) {
        console.error("contact save failed:", err);
      }
    },
    [contactData, fetchContactFromBackend]
  );

  // ---------- Enhanced Save Success Handler ----------
const handleSaveSuccess = useCallback(
  async (
    savedUUID,
    updatedData = null,
    stepNumber = currentStep,
    passedPropertyId = null,
    passedContactData = null
  ) => {
    const finalPropertyId = passedPropertyId || propertyId;
    const finalContactData = passedContactData || contactDataRef.current || contactData;

    // ✅ Step 1 → store data
    if (stepNumber === 1 && updatedData) {
      setPropertyFormData(updatedData);
    }

    // save contact — now guaranteed to have data if it was ever provided
    if (stepNumber === 1 && savedUUID && finalContactData) {
      await saveContactDetails(savedUUID, finalContactData);
    } else if (stepNumber === 1 && savedUUID) {
      console.warn('⚠️ Step 1 save: No contact data available');
    }

    // ✅ Step 2 → generate title
    if (stepNumber === 2 && updatedData) {
      if (!finalPropertyId) {
        console.error("PROPERTY_ID missing");
        return;
      }

      const attrs = normalizedPropertyDetails?.attributes || {};
      const base = normalizedPropertyDetails || {};

      // Try several likely backend key names for the property type
      const typeFromBackend =
        attrs.com_rent_property_type ??
        attrs.property_type ??
        attrs.commercial_property_type ??
        attrs.commercial_rent_property_type ??
        base.com_rent_property_type ??
        base.property_type ??
        base.commercial_property_type ??
        undefined;

      const mergedData = {
        ...propertyFormData,
        ...updatedData,
        propertyType:
          propertyFormData?.propertyType ??
          updatedData?.propertyType ??
          typeFromBackend,
        villageName:
          updatedData?.villageName ||
          propertyFormData?.villageName ||
          "Kanyakumari",
      };

      console.log("🏷️ Title generation inputs:", {
        propertyFormData,
        updatedData,
        attrs,
        typeFromBackend,
        mergedData,
      });

      const propertyTitle = generatePropertyTitle(
        mergedData,
        attributeValues,
        {
          bhkKey: "bhkType",
          typeKey: "propertyType",
          postedFor: "Rent",
        }
      );

      console.log("🏷️ Generated title:", propertyTitle);

      await callApi("PUT", "/properties/", {
        PROPERTY_ID: Number(finalPropertyId),
        PROPERTY_TITLE: propertyTitle,
      });
    }

    onSaveSuccess?.(savedUUID, updatedData, stepNumber);
    
  },
  [
  propertyId,
  propertyFormData,
  normalizedPropertyDetails,
  attributeValues,
  contactData,
  currentStep,
  onSaveSuccess,
  saveContactDetails,
]
);

  // ---------- Create property ----------
const createProperty = useCallback(
  async (formData = {}) => {
    try {
      const payload = {
        PROPERTY_TITLE: formData.propertyTitle || "Commercial Property",
        PROPERTY_DESCRIPTION:
          formData.propertyDescription ,
        PROPERTY_TYPE: "Commercial",
        PROPERTY_POSTED_FOR: "Rent",
        CREATED_BY_USER_ID: 1,
      };

      const res = await callApi("POST", "/properties/", payload);

      const newUUID =
        res?.data?.PROPERTY_UUID ||
        res?.data?.property_uuid ||
        res?.data?.uuid;

      const newPropertyId =
        res?.data?.PROPERTY_ID ||
        res?.data?.property_id ||
        res?.data?.id;

      if (!newUUID) throw new Error("No PROPERTY_UUID from /properties");

      setPropertyId(newPropertyId);
      setPropertyFormData(formData);

      // ✅ Pass contact data explicitly through the chain
      const contactToSave = contactDataRef.current || contactData;
      console.log('🏗️ createProperty: Passing contact data:', contactToSave);

      await handleSaveSuccess(newUUID, formData, 1, newPropertyId, contactToSave);

      // ✅ Only fetch backend contact if we didn't have it locally
      if (!contactToSave) {
        const backendContact = await fetchContactFromBackend(newUUID);
        if (backendContact) {
          setContactData((prev) => ({ ...prev, ...backendContact }));
        }
      }

      return newUUID;
    } catch (err) {
      console.error("createProperty failed:", err);
      setError("Failed to create property. Please try again.");
      throw err;
    }
  },
  [handleSaveSuccess, fetchContactFromBackend, contactData]
);

  // ---------- Save property details ----------
  const saveSectionAttributes = useCallback(
    async (uuid, attributes, formData, section = "property_detail") => {
      try {
        const payload = {
          property_uuid: uuid,
          category: "commercial", 
          subcategory: "rent",
          section,
          attributes,
          created_by: 1,
        };

        if (section === "property_detail") {
          payload.area_information = {
            built_up_area: Number(formData?.builtUpArea) || 0,
            carpet_area: 0,
            measurement_unit: Number(formData?.measurementUnit) || 1,
            plot_area: Number(formData?.plotArea) || 0,
          };
        }

        const resp = await callApi("POST", "/property_detail_attribute/", payload);
        setSuccess(`${section.replace("_", " ")} saved successfully!`);
        return { response: resp, formData };
      } catch (err) {
        console.error(`save ${section} failed:`, err);
        setError(`Failed to save ${section.replace("_", " ")}. Please try again.`);
        throw err;
      }
    },
    []
  );

  // ---------- Step Navigation ----------
  const handleStepChange = useCallback(
    (newStep, stepData = null) => {
      console.log('➡️ CommercialRentForm handleStepChange:', { from: currentStep, to: newStep });
      onStepChange?.(newStep, stepData);
    },
    [onStepChange, currentStep]
  );

  const handleNavigateNext = useCallback(
    (stepData = null) => {
      console.log('➡️ CommercialRentForm handleNavigateNext:', { currentStep, stepData });
      handleStepChange(currentStep + 1, stepData);
    },
    [currentStep, handleStepChange]
  );

  const handleNavigateBack = useCallback(
    () => {
      console.log('⬅️ CommercialRentForm handleNavigateBack:', { currentStep });
      handleStepChange(currentStep - 1);
    },
    [currentStep, handleStepChange]
  );

  // FIXED: Enhanced tab change handler to sync with parent
  const handleTabChange = useCallback(
    ({ selectedIndex }) => {
      const newStep = selectedIndex + 1;
      console.log('📑 Tab changed:', { selectedIndex, newStep, currentStep });
      
      // Only change step if it's different from current
      if (newStep !== currentStep) {
        handleStepChange(newStep);
      }
    },
    [handleStepChange, currentStep]
  );

  // ---------- Initialize ----------
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (attributeValues.length === 0) {
          await fetchAttributeValues();
        }
        if (mounted) setIsInitialized(true);
      } catch (e) {
        if (mounted) setError("Failed to initialize form");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [fetchAttributeValues, attributeValues.length]);

  // ---------- Steps ----------
  const steps = useMemo(
    () => [
      { key: "propertyDetails", label: "Property Details" },
      { key: "locationDetails", label: "Location Details" },
      { key: "rentalDetails", label: "Rental Details" },
      { key: "aminities", label: "Amenities" },
      { key: "additionalInfo", label: "Additional Info" },
      { key: "gallery", label: "Gallery" },
      { key: "schedule", label: "Schedule" },
    ],
    []
  );

  // ---------- Buttons Component ----------
  const CommonButtons = useCallback(
    ({
      onSave,
      onNext,
      showBack = true,
      showSave = true,
      showNext = true,
      isSaveLoading = false,
      stepData = null,
    }) => {
      const saveButtonText = isEditForm ? "Update" : "Save";

      const handleSaveClick = (e) => {
        e?.preventDefault();
        console.log('💾 Save button clicked - SAVE ONLY');
        onSave?.();
      };

      const handleNextClick = (e) => {
        e?.preventDefault();
        console.log('➡️ Next button clicked - SAVE AND NEXT');
        onNext?.(stepData);
      };

      const handleBackClick = (e) => {
        e?.preventDefault();
        console.log('⬅️ Back button clicked');
        handleNavigateBack();
      };

      return (
        <div
          style={{
            position: "fixed",
      bottom: "20px",
      left: "50%",
      transform: "translateX(-50%)",

      background: "#ffffff",
      padding: "12px 16px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",

      display: "flex",
      gap: "0.5rem",
      alignItems: "center",

      zIndex: 9999,
          }}
        >
          {/* ADDED: Back Button */}
          {showBack && currentStep > 1 && (
            <Button 
              kind="secondary" 
              onClick={handleBackClick} 
              disabled={isSaveLoading} 
              type="button" 
              size="md"
            >
              Back
            </Button>
          )}

          {showSave && (
            <Button 
              kind="secondary" 
              onClick={handleSaveClick} 
              disabled={isSaveLoading} 
              type="button" 
              size="md"
            >
              {isSaveLoading ? "Saving..." : saveButtonText}
            </Button>
          )}

          {showNext && (
            <Button 
              kind="primary" 
              onClick={handleNextClick} 
              disabled={isSaveLoading} 
              type="button" 
              size="md"
            >
              {isSaveLoading ? "Saving..." : "Next"}
            </Button>
          )}
        </div>
      );
    },
    [isEditForm, currentStep, handleNavigateBack]
  );

  // ---------- Render Step ----------
  const renderStep = useCallback(() => {
    const key = steps[stepIndex]?.key || steps[0].key;
    const totalSteps = steps.length;

  

    // FIXED: Use effectiveFormMode (from URL) for all mode-related props
    const commonProps = {
      data: normalizedPropertyDetails,
      propertyUuid: effectivePropertyUUID,
      request_type,
      currentStep,
      totalSteps,
      onBack: handleNavigateBack,
      attributeValues,
      isEditMode: isEditForm,
      CommonButtons,
      createProperty,
      saveSectionAttributes,
      onSaveSuccess: handleSaveSuccess,
      onNavigateNext: handleNavigateNext,
      contactData,
      formMode: effectiveFormMode,
      lastSavedStep,
    };

    switch (key) {
      case "propertyDetails":
        return <PropertyDetails {...commonProps} />;
      case "locationDetails":
        return <LocationDetails {...commonProps} />;
      case "rentalDetails":
        return <RentalDetails {...commonProps} />;
      case "aminities":
        return <Aminities {...commonProps} />;
      case "additionalInfo":
        return <AdditionalInfo {...commonProps} />;
      case "gallery":
        return <Gallery {...commonProps} showFooter />;
      case "schedule":
        return <Schedule {...commonProps} enforceValidation={false} callApiOnSubmit={false} />;
      default:
        return (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <h3>Step Configuration Error</h3>
            <p>Unable to render step: {key}</p>
          </div>
        );
    }
  }, [
    steps,
    stepIndex,
    normalizedPropertyDetails,
    effectivePropertyUUID,
    request_type,
    currentStep,
    handleNavigateBack,
    attributeValues,
    isEditForm,
    CommonButtons,
    createProperty,
    saveSectionAttributes,
    handleSaveSuccess,
    handleNavigateNext,
    contactData,
    effectiveFormMode,
    lastSavedStep,
  ]);

  // ---------- Loading gates ----------
  const editModeDataReady = !isEditForm || Object.keys(normalizedPropertyDetails || {}).length > 0;

  if (!isInitialized || isLoading || !editModeDataReady) {
    return (
      <div style={{ padding: "4rem", textAlign: "center" }}>
        <p>Loading form data...</p>
      </div>
    );
  }

  // ---------- Render ----------
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid fullWidth style={{ flex: 1 }}>
        {success && (
          <ToastNotification
            kind="success"
            title="Success"
            subtitle={success}
            timeout={2000}
            lowContrast
            onCloseButtonClick={() => setSuccess("")}
            style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: "300px" }}
          />
        )}

        {error && (
          <ToastNotification
            kind="error"
            title="Error"
            subtitle={error}
            timeout={5000}
            lowContrast
            onCloseButtonClick={() => setError("")}
            style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: "300px" }}
          />
        )}

        <Column sm={4} md={8} lg={16} style={{ padding: 0 }}>
          <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <div
              style={{
                padding: "2rem 2rem 1rem 2rem",
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#ffffff",
              }}
            ></div>
            <Tabs selectedIndex={stepIndex} onChange={handleTabChange}>
              <TabList 
                aria-label="Commercial rental listing steps"
                style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  width: "100%",
                  backgroundColor: "#f4f4f4",
                }}
              >
                {steps.map((s, index) => {
                  const isCompleted = lastSavedStep >= index + 1;
                  const isActive = index === stepIndex;
                  
                  return (
                    <Tab 
                      key={s.key} 
                      style={{ 
                        flex: 1, 
                        textAlign: "center",
                        borderBottom: isActive ? "3px solid #0f62fe" : "3px solid transparent",
                        backgroundColor: isActive ? "#ffffff" : "#ffffff",
                        fontWeight: isActive ? "600" : "400",
                        color: isActive ? "#0f62fe" : "#525252",
                        padding: "1rem 0.5rem",
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      {s.label}
                      {isCompleted }
                    </Tab>
                  );
                })}
              </TabList>




              <TabPanels>
                {steps.map((s, i) => (
                  <TabPanel key={`${s.key}-panel`}>
                    <div style={{ 
                      padding: "2rem",
                      backgroundColor: "#ffffff",
                      minHeight: "400px"
                    }}>
                      {i === stepIndex && renderStep()}
                    </div>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </div>
        </Column>
      </Grid>
    </div>
  );
}